import { useCallback, useEffect, useRef, useState } from "react";
import {
  AR_TILT_DEFAULT,
  AR_TILT_KEY,
  easeCorners,
  normalizeArTilt,
  tiltMatrix,
  type ArTiltSetting,
  type TiltCorners,
} from "@greg-chick/shared";
import { loadManifest, publishAssets, type ManifestEntry } from "../lib/contentApi";
import { Msg } from "./ui";

/**
 * 📐 **카메라 기울기** — 책을 비스듬히 들면 마법 그림도 같이 기운다.
 *
 * ## 왜 이 화면이 따로 있나
 * 이건 **카메라가 있어야 확인되는** 기능이다. 개발자도 헤드리스로는 못 본다 — 그래서
 * **기본을 꺼짐**으로 두고, 진짜 폰으로 보고 나서 운영자가 켠다. 켜고 끄는 자리가 없으면
 * 「이상하면 되돌릴 방법」이 배포뿐이 된다.
 *
 * ## 🔴 카메라 없이도 무엇인지 보이게
 * 말로만 「원근이 붙어요」 하면 켤지 말지 정할 수가 없다. 그래서 여기서 **기울기를 흉내 내서**
 * 보여 준다 — 아래 미리보기가 계산까지 아이 화면과 **같은 함수**(`tiltMatrix`)를 쓴다.
 * 따라가는 정도를 움직이면 그 자리에서 달라진다.
 */
export function ArTiltPanel() {
  const [cur, setCur] = useState<ArTiltSetting>(AR_TILT_DEFAULT);
  const [draft, setDraft] = useState<ArTiltSetting>(AR_TILT_DEFAULT);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  /** 흉내 낼 기울기(도). 운영자가 끌어 본다 — 카메라가 없으니 손으로 기울여 보는 셈이다. */
  const [fake, setFake] = useState(22);

  /**
   * 🔴 **늦게 온 답이 운영자의 선택을 덮으면 안 된다.**
   *
   * 서버가 느리면(엣지가 꺼져 있어 폴백까지 가는 경우) 불러오기가 **몇 초 뒤에** 끝난다.
   * 그 사이 운영자가 체크를 켜면, 도착한 값이 그 위에 덮여 **혼자 다시 꺼진다** — 누른 사람은
   * 「눌러도 안 켜진다」만 겪는다. 실제로 그랬다(2026-09-10, 진짜 서버에서 눌러 보고 알았다).
   * 한 번이라도 손댔으면 지금 보고 있는 것을 지킨다.
   */
  const touched = useRef(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const m = await loadManifest();
      const t = normalizeArTilt(m.assets[AR_TILT_KEY]);
      setCur(t);
      if (!touched.current) setDraft(t); // 손댄 뒤라면 화면을 안 건드린다
    } catch {
      // 🔴 「꺼져 있다」와 「못 물어봤다」는 다른 말이다 — 못 물어봤으면 그렇게 적는다
      setFailed(true);
    }
  }, []);

  /** 운영자가 바꾼 값 — 여기로만 바꾼다(손댔다는 표시가 같이 남는다). */
  const edit = (next: ArTiltSetting) => {
    touched.current = true;
    setDraft(next);
  };
  useEffect(() => {
    void load();
  }, [load]);

  const dirty = cur.on !== draft.on || cur.strength !== draft.strength;

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      // 🔴 게시 직전에 다시 읽는다 — 그 사이 다른 곳이 올렸을 수 있다(게시는 통째로 바꾸기다)
      const m = await loadManifest();
      const next: Record<string, ManifestEntry> = {
        ...m.assets,
        [AR_TILT_KEY]: { ...(m.assets[AR_TILT_KEY] ?? {}), url: "", on: draft.on, strength: draft.strength },
      };
      const out = await publishAssets(next);
      setCur(draft);
      touched.current = false; // 올렸으니 화면과 서버가 같다 — 다음 불러오기는 받아들여도 된다
      setMsg(`✅ ${draft.on ? "켰어요" : "껐어요"} (v${out.version}) — 카메라 AR 에 바로 적용돼요.`);
    } catch (e) {
      setMsg(`⚠️ 못 바꿨어요: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  // ── 미리보기: 손으로 기울인 책을 흉내 낸다 ──
  const W = 260;
  const H = 260;
  const rad = (fake * Math.PI) / 180;
  /** 오른쪽이 뒤로 넘어간 모습 — 먼 쪽이 좁아진다(원근). */
  const shrink = Math.sin(rad) * 0.42;
  const shift = Math.sin(rad) * 0.10 * W;
  const corners: TiltCorners = [
    { x: 0, y: 0 },
    { x: W - shift, y: H * shrink * 0.5 },
    { x: W - shift, y: H - H * shrink * 0.5 },
    { x: 0, y: H },
  ];
  const m = tiltMatrix(W, H, easeCorners(W, H, corners, draft.strength));
  const css = draft.on && m ? `matrix3d(${m.join(",")})` : undefined;

  return (
    <section className="artilt">
      <p className="hint">
        아이가 카메라로 책을 비출 때, 책을 <b>비스듬히 들면 마법 그림도 같이 기울어요</b>.
        글자(설명·손잡이)는 <b>안 기울어요</b> — 흔들리면 못 읽으니까요.
      </p>

      {failed ? (
        <>
          <p className="hint">📐 지금 켜져 있는지 <b>못 물어봤어요</b>(서버 연결).</p>
          <button type="button" className="btn btn--sm" onClick={() => void load()}>다시 확인</button>
        </>
      ) : (
        <>
          <div className="artilt__row">
            <label className="bk-check">
              <input type="checkbox" checked={draft.on} onChange={(e) => edit({ ...draft, on: e.target.checked })} />
              <b>카메라 기울기 켜기</b>
            </label>
            <span className="hint">지금 아이 화면: {cur.on ? "켜짐" : "꺼짐"}</span>
          </div>

          <label className="artilt__slider">
            얼마나 따라갈까 — <b>{Math.round(draft.strength * 100)}%</b>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(draft.strength * 100)}
              onChange={(e) => edit({ ...draft, strength: Number(e.target.value) / 100 })}
            />
          </label>
          <p className="hint">
            100%면 책을 그대로 따라가요. 손이 떨리면 그림도 같이 떨리니, <b>80% 안팎</b>이 보기 편해요.
          </p>

          <div className="artilt__demo">
            <label className="artilt__slider">
              📐 이만큼 기울여 보기 — <b>{fake}도</b>
              <input type="range" min={0} max={55} value={fake} onChange={(e) => setFake(Number(e.target.value))} />
            </label>
            {/* 카메라가 없으니 손으로 기울여 본다. 계산은 아이 화면과 **같은 함수**다. */}
            <div className="artilt__stage" style={{ width: W, height: H }}>
              <div className="artilt__paper" style={{ transform: css, transformOrigin: "0 0" }}>
                <span className="artilt__crown" aria-hidden="true">👑</span>
                <span className="artilt__note">이 그림이 기울어요</span>
              </div>
            </div>
            <p className="hint">
              {draft.on
                ? "이렇게 책을 따라 기울어요. 글자는 이 상자 밖에 있어서 늘 똑바로 보여요."
                : "지금은 꺼져 있어서 늘 정면으로만 보여요. 켜 보면 위 그림이 기울어요."}
            </p>
          </div>

          <div className="artilt__row">
            <button type="button" className="btn btn--primary" disabled={busy || !dirty} onClick={() => void save()}>
              {busy ? "바꾸는 중…" : dirty ? "아이 화면에 적용" : "바뀐 것 없음"}
            </button>
            {dirty ? (
              <button type="button" className="btn btn--sm" onClick={() => edit(cur)}>되돌리기</button>
            ) : null}
          </div>

          {/* 🔴 켜기 전에 **폰으로 보라**고 적는다. 여기서는 흉내만 낸 것이다. */}
          <p className="hint">
            ⚠️ 위 미리보기는 <b>흉내</b>예요. 켜기 전에 진짜 폰으로 책을 비춰 보고, 기울어진 그림이
            책 위에 잘 붙는지 확인해 주세요. 이상하면 여기서 바로 끌 수 있어요.
          </p>
        </>
      )}
      <Msg text={msg} />
    </section>
  );
}
